import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import { Form, Icon, Row, Col } from 'antd';
import BasePage from 'components/BasePage';
import styledComp, { FlexColContainer, FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import FormDatePicker from 'components-desktop/FormDatePicker';
import FormInput from 'components-desktop/FormInput';
import FormSelect from 'components-desktop/FormSelect';
import FormSelect2 from 'components-desktop/FormSelect2';
import Panel from 'components-desktop/Panel';
import helpers from 'misc/helpers';
import TablePatientsVcs from './components/TablePatientsVcs';

const propTypes = {
  form: PropTypes.object.isRequired,
};

@autobind
class SearchPatientsVcsPage extends BasePage {
  constructor(props) {
    super(props);

    this.state = {
      companies: [],
      patientNames: [],
      caregivers: [],
      coordinators: [],
      teams: [],

      tableKey: null,
      patients: null,
      patientContracts: null,
      isSearchButtonEnable: true,
    };
  }

  componentDidMount() {
    this.getSearchCriteria();
  }

  getTitle() {
    return 'Search Patients';
  }

  async getSearchCriteria() {
    const promise1 = this.callServiceForObjects('/company/get_id_names');
    const promise2 = this.callServiceForObjects('/patient/get_id_names');
    const promise3 = this.callServiceForObjects('/caregiver/get_id_names');
    const promise4 = this.callServiceForObjects('/coordinator/get_id_names');
    const promise5 = this.callServiceForObjects('/team/get_id_names');

    const results = await Promise.all([promise1, promise2, promise3, promise4, promise5]);

    this.setState({
      companies: results[0],
      patientNames: results[1],
      caregivers: results[2],
      coordinators: results[3],
      teams: results[4],
    });
  }

  async searchPatients(values) {
    try {
      this.setState({
        isSearchButtonEnable: false,
      });

      const params = {
        companyId: helpers.extraFormSelectValue(values, 'companyId'),
        patientId: helpers.extraFormSelectValue(values, 'patientId'),
        caregiverId: helpers.extraFormSelectValue(values, 'caregiverId'),
        coordinatorId: helpers.extraFormSelectValue(values, 'coordinatorId'),
        teamId: helpers.extraFormSelectValue(values, 'teamId'),
        birth: helpers.extraFormDateValue(values, 'birth'),
        gender: helpers.extraFormSelectValue(values, 'gender'),
        ssn: helpers.extraFormInputValue(values, 'ssn'),
        language: helpers.extraFormInputValue(values, 'language'),
        medicaidNo: helpers.extraFormInputValue(values, 'medicaidNo'),
        medicareNo: helpers.extraFormInputValue(values, 'medicareNo'),
        contractPackage: helpers.extraFormSelectValue(values, 'contractPackage'),
        applyStatus: helpers.extraFormSelectValue(values, 'applyStatus'),
        contractStatus: helpers.extraFormSelectValue(values, 'contractStatus'),
        borough: helpers.extraFormSelectValue(values, 'borough'),
      };

      const patients = await this.callServiceForRemoteObjects('/patient/get_list', params);

      // Also get the latest contract for each patient.
      const params2 = {
        patientIds: patients.map(p => p.getId()),
      };
      const patientContracts =
        await this.callServiceForRemoteObjects('/contract/get_latest_active_for_patients', params2);

      this.setState({
        tableKey: `${Date.now()}_${Math.random()}`,
        patients,
        patientContracts,
      });
    } finally {
      this.setState({
        isSearchButtonEnable: true,
      });
    }
  }

  handleSubmit(e) {
    e.preventDefault();

    this.props.form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        this.searchPatients(values);
      }
    });
  }

  renderContent() {
    const { form } = this.props;
    const {
      companies,
      patientNames,
      caregivers,
      coordinators,
      teams,
      tableKey,
      patients,
      patientContracts,
      isSearchButtonEnable,
    } = this.state;

    return (
      <FlexColContainer
        width="match_parent"
        height="wrap_content"
        alignItems="center"
      >
        <Panel
          width="match_parent"
          height="wrap_content"
          margin="0 0 24px 0"
          color="blue_steel"
          title="Search Patients by Criteria"
          icon="user"
        >
          <styledComp.AntdForm
            width="match_parent"
            height="wrap_content"
            onSubmit={this.handleSubmit}
          >
            {/* Content */}
            <FlexColContainer
              width="match_parent"
              height="wrap_content"
              padding="20px 20px"
            >
              <Row type="flex" gutter={24}>
                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="companyId"
                    label="Company"
                    placeholder="Select..."
                    options={companies}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect2
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="patientId"
                    label="Patient Name"
                    placeholder="Select..."
                    options={patientNames}
                    showIdAfterName
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect2
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="caregiverId"
                    label="Caregiver"
                    placeholder="Select..."
                    options={caregivers}
                    showIdAfterName
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="coordinatorId"
                    label="Coordinator"
                    placeholder="Select..."
                    options={coordinators}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="teamId"
                    label="Team"
                    placeholder="Select..."
                    options={teams}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormDatePicker
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="birth"
                    label="Date of Birth"
                    placeholder="mm/dd/yyyy"
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="gender"
                    label="Gender"
                    placeholder="Select..."
                    options={['Female', 'Male']}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="ssn"
                    label="Social Security Number (SSN)"
                    placeholder="111-11-1111"
                    inputMask="999-99-9999"
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="language"
                    label="Language"
                    placeholder=""
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="medicaidNo"
                    label="Medicaid No"
                    placeholder=""
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="medicareNo"
                    label="Medicare No"
                    placeholder=""
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="borough"
                    label="Borough"
                    placeholder="Select..."
                    options={['Bronx', 'Brooklyn', 'Manhattan', 'Queens', 'Staten Island', 'Other']}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="applyStatus"
                    label="Apply Status"
                    placeholder="Select..."
                    initialValue="APPROVED"
                    options={['PROCESSING', 'APPROVED', 'REJECTED', 'CANCELED', 'ON_HOLD', 'DISCHARGED']}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="contractStatus"
                    label="Contract Status"
                    placeholder="Select..."
                    options={['Active', 'Discharged']}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="contractPackage"
                    label="Contract Package"
                    placeholder="Select..."
                    options={[{ value: 'true', text: 'Yes' }, { value: 'false', text: 'No' }]}
                  />
                </Col>
              </Row>
            </FlexColContainer>

            {/* Footer */}
            <FlexRowContainer
              width="match_parent"
              height="wrap_content"
              padding="20px 20px"
              justifyContent="center"
              backgroundColor="containerGrayBackground"
            >
              <CustomButton
                width="wrap_content"
                height="32px"
                backgroundColor="blue_steel"
                fontSize="14px"
                type="primary"
                loading={!isSearchButtonEnable}
                htmlType="submit"
              >
                SEARCH
                <Icon type="search" />
              </CustomButton>
            </FlexRowContainer>
          </styledComp.AntdForm>
        </Panel>

        {patients && patientContracts &&
          <TablePatientsVcs
            key={tableKey}
            width="match_parent"
            height="wrap_content"
            margin="0 0 24px 0"
            title="Patients"
            patients={patients}
            patientContracts={patientContracts}
          />
        }
      </FlexColContainer>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
SearchPatientsVcsPage.propTypes = propTypes;

export default Form.create()(SearchPatientsVcsPage);
